import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess } from "Components/constants/Common";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { BaseSelect, MultiSelect } from "Components/Base/BaseSelect";
import {
  cancelLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import {
  CHASSIS_NO_LABEL,
  CUSTOMER_COMPLAINTS_LABEL,
  CUSTOMER_ID_LABEL,
  CUSTOMER_NAME_LABEL,
  EDIT_REPAIR_ORDER_LABEL,
  ENGINE_HOUR_LABEL,
  ENGINE_NO_LABEL,
  GEAR_BOX_NO_LABEL,
  KM_LABEL,
  NEW_REPAIR_ORDER_LABEL,
  QUOTAION_NO_LABEL,
  REGIATRTION_NO_LABEL,
  REPAIR_ORDER_TYPE_LABEL,
  REPAIR_PARTS_ORDER_LABEL,
  RepairOrderTypeList,
  SITE_LOCATION_LABEL,
  START_DATE_LABEL,
  VDN_NO_LABEL,
  WORK_ORDER_NO_LABEL,
} from "Components/constants/RepairOrderOpening";
import {
  createROO,
  getWorkOrderList,
  updateROO,
  viewROO,
} from "Api/RepairOrderOpen";
import { AxiosError } from "axios";
import { listOfCustomer } from "Api/customer";
import { listOfTechnician } from "Api/Technician";
import { TECHNICIAN_NAME } from "Components/constants/Technician";

const validationSchema = yup.object({
  repair_part_order: yup
    .string()
    .required(validationMessages.required("Repair/Parts Order")),
  start_date: yup.date().required(validationMessages.required("Start Date")),
});

interface Quotation {
  quotation_number: string;
}

interface WorkOrderOption {
  value: string;
  label: string;
  quotation?: Quotation;
}

interface CustomerOption {
  value: string;
  label: string;
  user_draft_id: string;
}

interface TechnicianOption {
  value: string;
  label: string;
}

const ROOform = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loader, setLoader] = useState(false);
  const [btnLoader, setBtnLoader] = useState(false);
  const [viewROOData, setViewROOData] = useState<any>([]);
  const [workOrderOptions, setWorkOrderOptions] = useState<WorkOrderOption[]>(
    []
  );
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [technicianOptions, setTechnicianOptions] = useState<
    TechnicianOption[]
  >([]);

  const [selectedQuotationNumber, setSelectedQuotationNumber] = useState<
    string | null
  >(null);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(1000);
  const [selectedMulti, setSelectedMulti] = useState<any>("");

  const fetchWorkOrderDropdownOptions = () => {
    setLoader(true);
    getWorkOrderList()
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setWorkOrderOptions(
            res?.data?.map((item: any) => ({
              value: item?.id,
              label: item?.work_order_number,
              quotation: item?.quotation,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchCustomerDropdownOptions = () => {
    setLoader(true);
    const payload = {
      sortKey: columnName,
      sortValue: sortOrder,
      page: currentPage,
      type_of_user: "Customer",
      limit: customPageSize,
    };
    listOfCustomer(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setCustomerOptions(
            res?.data?.positions?.map((item: any) => ({
              value: item?.id,
              label: item?.name,
              user_draft_id: item?.user_draft_id,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchTechnicianDropdownOptions = () => {
    setLoader(true);
    const payload = {
      sortKey: columnName,
      sortValue: sortOrder,
      page: currentPage,
      limit: customPageSize,
    };
    listOfTechnician(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setTechnicianOptions(
            res?.data?.positions?.map((item: any) => ({
              value: item?.id,
              label: item?.technician_name,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleWorkOrderChange = (selectedWorkOrderId: string | null) => {
    if (selectedWorkOrderId) {
      const selectedWorkOrder = workOrderOptions.find(
        (item) => item.value === selectedWorkOrderId
      );

      if (selectedWorkOrder && selectedWorkOrder.quotation) {
        const quotationNumber = selectedWorkOrder.quotation.quotation_number;
        setSelectedQuotationNumber(quotationNumber);
        formik.setFieldValue("quotation_id", quotationNumber);
      } else {
        setSelectedQuotationNumber(null);
        formik.setFieldValue("quotation_id", "");
      }
    } else {
      setSelectedQuotationNumber(null);
      formik.setFieldValue("quotation_id", "");
    }
  };

  const handleCustomerChange = (selectedCustomerId: string | null) => {
    if (selectedCustomerId) {
      const selectedCustomer = customerOptions.find(
        (item) => item.value === selectedCustomerId
      );

      if (selectedCustomer) {
        formik.setFieldValue("user_draft_id", selectedCustomer.user_draft_id);
      } else {
        formik.setFieldValue("user_draft_id", "");
      }
    } else {
      formik.setFieldValue("user_draft_id", "");
    }
  };

  const viewFetchedROOData = async () => {
    if (id) {
      setLoader(true);
      try {
        const res = await viewROO(Number(id));
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setViewROOData(res?.data);
          formik.setFieldValue("work_order_id", res?.data?.work_order_id);
        } else {
          toast.error(res?.message);
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          toast.error(error?.response?.data?.message || error?.message);
        }
      } finally {
        setLoader(false);
      }
    }
  };
  function handleMulti(selectedOptions: any) {
    if (!selectedOptions) {
      formik.setFieldValue("technician", []);
      setSelectedMulti([]);
      return;
    }

    const selectedValues = Array.isArray(selectedOptions)
      ? selectedOptions.map((option) => option.value)
      : [];

    formik.setFieldValue("technician", selectedValues);
    setSelectedMulti(selectedOptions);
  }

  const formik = useFormik({
    enableReinitialize: !!id,
    initialValues: {
      repair_part_order: id ? viewROOData?.repair_part_order || "" : "",
      start_date: id
        ? viewROOData?.start_date || new Date().toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      technician: id
        ? viewROOData?.technician?.map((item: any) => item?.technician?.id) ||
          []
        : [],
      site_location: id ? viewROOData?.site_location || "" : "",
      km: id ? viewROOData?.km || 0 : 0,
      repair_order_type: id ? viewROOData?.repair_order_type || null :null,
      registration_no: id ? viewROOData?.registration_no || "" : "",
      vdn_no: id ? viewROOData?.vdn_no || "" : "",
      gear_box_no: id ? viewROOData?.gear_box_no || "" : "",
      chassis_no: id ? viewROOData?.chassis_no || "" : "",
      quotation_id: id
        ? viewROOData?.quotation_number || selectedQuotationNumber || ""
        : selectedQuotationNumber || "",
      customer_complains: id ? viewROOData?.customer_complains || "" : "",
      work_order_id: id ? viewROOData?.work_order_id || "" : "",
      customer_id: id ? viewROOData?.customer_id || null : null,
      user_draft_id: id ? viewROOData?.user_draft_id || "" : "",
      engine_hour: id ? viewROOData?.engine_hour || "" : "",
      engine_no: id ? viewROOData?.engine_no || "" : "",
    },
    validationSchema,
    onSubmit: (values) => {
      const technicianData = values.technician.map((id: any) => ({
        technician_id: id,
      }));
      setBtnLoader(true);
      const payload = {
        repair_part_order: values.repair_part_order,
        start_date: values.start_date,
        technician: technicianData,
        site_location: values.site_location,
        km: values.km,
        repair_order_type: values.repair_order_type || null,
        registration_no: values.registration_no,
        vdn_no: values.vdn_no,
        gear_box_no: values.gear_box_no,
        chassis_no: values.chassis_no,
        quotation_number: values.quotation_id,
        customer_complains: values.customer_complains,
        work_order_id: Number(values.work_order_id) || null,
        user_draft_id: values.user_draft_id,
        customer_id: values.customer_id,
        engine_hour: Number(values.engine_hour),
        engine_no: values.engine_no,
      };

      if (id) {
        updateROO(Number(id), payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              navigate("/repairOrder");
            } else {
              toast.error(res?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
          })
          .finally(() => {
            setBtnLoader(false);
          });
      } else {
        createROO(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              navigate("/repairOrder");
            } else {
              toast.error(res?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
          })
          .finally(() => {
            setBtnLoader(false);
          });
      }
    },
  });

  useEffect(() => {
    if (id) {
      viewFetchedROOData();
    }

    fetchWorkOrderDropdownOptions();
    fetchCustomerDropdownOptions();
    fetchTechnicianDropdownOptions();
  }, []);

  document.title = id ? EDIT_REPAIR_ORDER_LABEL : NEW_REPAIR_ORDER_LABEL;

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">
            {id ? "Edit Repair Order" : "New Repair Order"}
          </span>
          <button
            className="btn btn-danger"
            onClick={() => navigate("/repairOrder")}
          >
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <form onSubmit={formik.handleSubmit} className="mt-2 mb-3">
              <div className="row">
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseInput
                    name="repair_part_order"
                    label={REPAIR_PARTS_ORDER_LABEL}
                    placeholder={InputPlaceHolder(REPAIR_PARTS_ORDER_LABEL)}
                    value={formik.values.repair_part_order}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.repair_part_order}
                    touched={formik.touched.repair_part_order}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseInput
                    name="start_date"
                    type="date"
                    label={START_DATE_LABEL}
                    placeholder={InputPlaceHolder(START_DATE_LABEL)}
                    value={formik.values.start_date}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.start_date}
                    touched={formik.touched.start_date}
                    required
                  />
                </div>

                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseSelect
                    name="customer_id"
                    label={CUSTOMER_NAME_LABEL}
                    options={customerOptions}
                    value={formik.values.customer_id}
                    placeholder={InputPlaceHolder(CUSTOMER_NAME_LABEL)}
                    handleChange={(field: string, value: any) => {
                      formik.setFieldValue(field, value);
                      handleCustomerChange(value);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.errors.customer_id}
                    touched={formik.touched.customer_id}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseInput
                    name="user_draft_id"
                    label={CUSTOMER_ID_LABEL}
                    placeholder={InputPlaceHolder(CUSTOMER_ID_LABEL)}
                    value={formik.values.user_draft_id}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.user_draft_id}
                    touched={formik.touched.user_draft_id}
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <MultiSelect
                    name="technician"
                    label={TECHNICIAN_NAME}
                    options={technicianOptions}
                    isMulti={true}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.technician}
                    touched={formik.touched.technician}
                    placeholder={InputPlaceHolder(TECHNICIAN_NAME)}
                    value={technicianOptions.filter((option) =>
                      formik.values.technician.includes(option.value)
                    )}
                    isDisabled={false}
                    onChange={handleMulti}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseInput
                    name="site_location"
                    label={SITE_LOCATION_LABEL}
                    placeholder={InputPlaceHolder(SITE_LOCATION_LABEL)}
                    value={formik.values.site_location}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.site_location}
                    touched={formik.touched.site_location}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseInput
                    name="km"
                    label={KM_LABEL}
                    type="number"
                    placeholder={InputPlaceHolder(KM_LABEL)}
                    value={formik.values.km}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.km}
                    touched={formik.touched.km}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseSelect
                    name="repair_order_type"
                    label={REPAIR_ORDER_TYPE_LABEL}
                    options={RepairOrderTypeList}
                    value={formik.values.repair_order_type}
                    placeholder={SelectPlaceHolder(REPAIR_ORDER_TYPE_LABEL)}
                    handleChange={(field: string, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.errors.repair_order_type}
                    touched={formik.touched.repair_order_type}
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseInput
                    name="engine_hour"
                    label={ENGINE_HOUR_LABEL}
                    type="number"
                    placeholder={InputPlaceHolder(ENGINE_HOUR_LABEL)}
                    value={formik.values.engine_hour}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.engine_hour}
                    touched={formik.touched.engine_hour}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseInput
                    name="registration_no"
                    label={REGIATRTION_NO_LABEL}
                    placeholder={InputPlaceHolder(REGIATRTION_NO_LABEL)}
                    value={formik.values.registration_no}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.registration_no}
                    touched={formik.touched.registration_no}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseInput
                    name="vdn_no"
                    label={VDN_NO_LABEL}
                    placeholder={InputPlaceHolder(VDN_NO_LABEL)}
                    value={formik.values.vdn_no}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.vdn_no}
                    touched={formik.touched.vdn_no}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseInput
                    name="gear_box_no"
                    label={GEAR_BOX_NO_LABEL}
                    placeholder={InputPlaceHolder(GEAR_BOX_NO_LABEL)}
                    value={formik.values.gear_box_no}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.gear_box_no}
                    touched={formik.touched.gear_box_no}
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-3 col-sm-6 col-12 mb-2">
                  <BaseInput
                    name="engine_no"
                    label={ENGINE_NO_LABEL}
                    placeholder={InputPlaceHolder(ENGINE_NO_LABEL)}
                    value={formik.values.engine_no}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.engine_no}
                    touched={formik.touched.engine_no}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mb-3">
                  <BaseInput
                    name="chassis_no"
                    label={CHASSIS_NO_LABEL}
                    placeholder={InputPlaceHolder(CHASSIS_NO_LABEL)}
                    value={formik.values.chassis_no}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.chassis_no}
                    touched={formik.touched.chassis_no}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mb-3">
                  <BaseSelect
                    name="work_order_id"
                    label={WORK_ORDER_NO_LABEL}
                    options={workOrderOptions}
                    value={formik.values.work_order_id}
                    placeholder={SelectPlaceHolder(WORK_ORDER_NO_LABEL)}
                    handleChange={(field: string, value: any) => {
                      formik.setFieldValue(field, value);
                      handleWorkOrderChange(value);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.errors.work_order_id}
                    touched={formik.touched.work_order_id}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mb-3">
                  <BaseInput
                    name="quotation_id"
                    label={QUOTAION_NO_LABEL}
                    placeholder={InputPlaceHolder(QUOTAION_NO_LABEL)}
                    value={
                      formik.values.quotation_id ||
                      selectedQuotationNumber ||
                      ""
                    }
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.quotation_id}
                    touched={formik.touched.quotation_id}
                    disabled
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-3 col-sm-6 col-12 mb-3">
                  <BaseInput
                    name="customer_complains"
                    label={CUSTOMER_COMPLAINTS_LABEL}
                    type="textarea"
                    placeholder={InputPlaceHolder(CUSTOMER_COMPLAINTS_LABEL)}
                    value={formik.values.customer_complains}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.customer_complains}
                    touched={formik.touched.customer_complains}
                  />
                </div>
              </div>

              <div className="row mt-3 d-flex justify-content-end">
                <div className="col-auto d-flex justify-content-end gap-2 text-end">
                  <BaseButton
                    type="submit"
                    label={id ? updateLabel : submitLabel}
                    color="success"
                    loader={btnLoader}
                    disable={btnLoader}
                  />
                  <BaseButton
                    type="button"
                    label={cancelLabel}
                    onClick={() => navigate("/repairOrder")}
                    color="danger"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROOform;
